/**
 * iter101 — Passive Match Reveal.
 *
 * The problem: 42 signed up · 38 completed intake · 32 opted in ·
 * only 4 created a first Moment. The current "opt in first, then see
 * peers" wall was the drop-off. Case data already carries host_university
 * + start_date for every confirmed student. This is a display problem,
 * not a data problem.
 *
 * This card is the new front door to Cohort. No opt-in required to see
 * peers, no destination-intent step, no "we'll match you soon" waiting
 * room. If two people are heading to the same host university in the
 * same semester band, they see each other. If three or more are heading
 * to the same host university, the group chat is auto-created.
 *
 * Design rails:
 *   · Progressive disclosure — collapsed pill by default, chevron opens
 *     inline (no page navigation, no modal).
 *   · One primary action per state — the collapsed pill has ONE tap
 *     target (open); the expanded row has ONE per peer ("Say hi").
 *   · Faces (initials) before text.
 *   · Neptune palette · indigo #525AFF (--moe-elec), teal accent.
 *   · No urgency styling. This is a reassurance moment.
 *   · Persistent slim line after first acknowledgment — not a full card
 *     every visit.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowRight, ChevronDown, ChevronUp, MessageCircle, Users, Check } from "lucide-react";

const AVATAR_TONES = [
  "bg-elec/15  text-elec  ring-elec/30",
  "bg-teal-500/15 text-teal-700 ring-teal-500/30",
  "bg-indigo-500/15 text-indigo-700 ring-indigo-500/30",
  "bg-cyan-500/15 text-cyan-700 ring-cyan-500/30",
];

function initialsOf(name) {
  if (!name) return "·";
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "·";
  const first = parts[0][0] || "";
  const last  = parts.length > 1 ? parts[parts.length - 1][0] : "";
  return (first + last).toUpperCase();
}

function Avatar({ name, i = 0, size = "sm" }) {
  const dim = size === "lg" ? "w-11 h-11 text-sm" : "w-8 h-8 text-[11px]";
  const tone = AVATAR_TONES[i % AVATAR_TONES.length];
  return (
    <div
      className={`${dim} rounded-full flex items-center justify-center font-bold tracking-tight ring-2 ring-white ${tone}`}
      aria-label={name}
    >
      {initialsOf(name)}
    </div>
  );
}

function AvatarStack({ peers, max = 4 }) {
  const shown = peers.slice(0, max);
  const more = Math.max(0, peers.length - max);
  return (
    <div className="flex items-center -space-x-2" aria-hidden="false">
      {shown.map((p, i) => (
        <Avatar key={p.case_id} name={p.display_name} i={i} />
      ))}
      {more > 0 && (
        <div className="w-8 h-8 rounded-full ring-2 ring-white bg-ink/10 text-ink/70 flex items-center justify-center text-[11px] font-bold">
          +{more}
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */

export default function PassiveMatchCard({ hostCity }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [saying, setSaying]     = useState({}); // { case_id: "loading" | "sent" | "error" }
  const navigate = useNavigate();

  const load = async () => {
    try {
      setLoading(true);
      const r = await api.get("/manifest/passive-matches");
      setData(r.data);
      setError(null);
    } catch (e) {
      // Failing quietly is deliberate — this is an additive card, not a
      // required flow. A visible error here would create the exact
      // "action required" texture the founder ruled out.
      setError(e?.response?.data?.detail || "unavailable");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const acknowledged = !!data?.acknowledged_at;
  const total        = data?.total || 0;
  const sameCampus   = data?.same_campus || [];
  const nearby       = data?.nearby || [];
  const allPeers     = useMemo(() => [...sameCampus, ...nearby], [sameCampus, nearby]);
  const assembly     = data?.assembly || null;

  // Declare acknowledgeIfNeeded BEFORE the useEffect that references it AND
  // before any early-return branches — otherwise a render that early-returns
  // leaves the const in TDZ and the effect closure crashes on execution.
  const acknowledgeIfNeeded = async () => {
    if (acknowledged) return;
    try {
      await api.post("/manifest/passive-matches/acknowledge", { acknowledged: true });
      setData((d) => ({ ...d, acknowledged_at: new Date().toISOString() }));
    } catch { /* silent — non-blocking UX */ }
  };

  // Default-open on first visit (no acknowledgment yet). Once acknowledged
  // the pill stays collapsed until the user taps it again. The moment the
  // card is expanded (auto OR by tap), we stamp acknowledged_at server-side
  // so the second visit renders the slim persistent line.
  useEffect(() => {
    if (data && !acknowledged) {
      setExpanded(true);
      acknowledgeIfNeeded();
    }
    if (data && acknowledged) setExpanded(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, acknowledged]);

  // No card at all when there are literally zero peers — never fabricate
  // "you're the first one here" copy on top of a data endpoint error.
  if (loading) {
    return (
      <div className="mt-6" data-testid="passive-match-card-loading">
        <div className="moe-shimmer h-14 rounded-2xl" />
      </div>
    );
  }
  if (error || !data || total === 0) return null;

  /* ------------------------------------------------------------------ */
  const primaryHeadline = (() => {
    if (sameCampus.length >= 1 && nearby.length >= 1) {
      return `${sameCampus.length} at your host uni · ${nearby.length} nearby, same term`;
    }
    if (sameCampus.length >= 1) {
      const city = sameCampus[0]?.host_city || hostCity || "your host city";
      return sameCampus.length === 1
        ? `You're not going alone. 1 peer, same campus.`
        : `You're not going alone. ${sameCampus.length} peers, same campus in ${city}.`;
    }
    return nearby.length === 1
      ? `1 peer in ${hostCity || nearby[0]?.host_city || "your host city"}, same term.`
      : `${nearby.length} peers in ${hostCity || nearby[0]?.host_city || "your host city"}, same term.`;
  })();

  const toggle = () => {
    const next = !expanded;
    setExpanded(next);
    if (next) acknowledgeIfNeeded();
  };

  const sayHi = async (peer) => {
    if (saying[peer.case_id]) return;
    setSaying((s) => ({ ...s, [peer.case_id]: "loading" }));
    try {
      // iter103 (Priority 1) — Say-hi now returns a conversation_id and
      // routes the sender straight into the live thread. Previously the
      // button flipped to "Said hi" and stranded the sender with no
      // surface to click into.
      const r = await api.post("/manifest/say-hi", { target_case_id: peer.case_id });
      const convoId = r?.data?.conversation_id;
      setSaying((s) => ({ ...s, [peer.case_id]: "sent" }));
      if (convoId) {
        navigate(`/dashboard/manifest?tab=messages&convo=${convoId}`);
      }
    } catch (e) {
      setSaying((s) => ({ ...s, [peer.case_id]: "error" }));
    }
  };

  /* ------------------------------------------------------------------ */

  return (
    <section
      className="mt-6"
      data-testid="passive-match-card"
      data-total={total}
      data-assembly={assembly ? "1" : "0"}
      aria-label="Peers on your exchange"
    >
      {/* Collapsed pill · one tap target. The peers-headline text is the
          entire tap surface, chevron is a visual affordance not a separate
          button — Hick's Law. */}
      <button
        type="button"
        onClick={toggle}
        data-testid="passive-match-toggle"
        aria-expanded={expanded}
        className={`w-full flex items-center gap-3 md:gap-4 rounded-2xl border transition-all group text-left
          ${expanded
            ? "border-elec/40 bg-white shadow-[0_1px_0_rgba(82,90,255,0.08)] px-4 py-3 md:px-5 md:py-4"
            : acknowledged
              ? "border-ink/10 bg-white/60 hover:bg-white hover:border-ink/25 px-4 py-2.5"
              : "border-elec/25 bg-gradient-to-r from-elec/[0.04] to-teal-500/[0.03] hover:from-elec/[0.06] hover:to-teal-500/[0.05] hover:border-elec/40 px-4 py-3 md:px-5 md:py-4"
          }`}
      >
        <AvatarStack peers={allPeers} max={acknowledged && !expanded ? 3 : 4} />
        <div className="min-w-0 flex-1">
          <div className={`font-bold text-ink leading-tight ${acknowledged && !expanded ? "text-[13px]" : "text-[14px] md:text-[15px]"}`} data-testid="passive-match-headline">
            {primaryHeadline}
          </div>
          {!acknowledged && !expanded && (
            <div className="mt-0.5 text-[12px] text-ink/60 leading-snug">
              A few names, one tap to say hi. No opt-in wall.
            </div>
          )}
        </div>
        {expanded
          ? <ChevronUp size={16} className="text-elec shrink-0" />
          : <ChevronDown size={16} className="text-elec shrink-0 group-hover:translate-y-0.5 transition-transform" />
        }
      </button>

      {/* Expanded state — inline, no modal, no route change. */}
      {expanded && (
        <div
          className="mt-3 rounded-2xl border border-ink/10 bg-white overflow-hidden"
          data-testid="passive-match-expanded"
        >
          {assembly && (
            <div
              className="flex items-start gap-3 px-4 py-3 md:px-5 md:py-3.5 border-b border-ink/10 bg-teal-500/[0.06]"
              data-testid="passive-assembly-banner"
            >
              <div className="w-9 h-9 rounded-full bg-teal-500/15 text-teal-700 flex items-center justify-center shrink-0">
                <Users size={16} strokeWidth={2.2}/>
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-bold text-ink leading-snug">
                  {assembly.member_count} of you are here. Group chat created.
                </div>
                <div className="mt-0.5 text-[12px] text-ink/70 leading-snug">
                  A room for everyone heading to {assembly.host_university} this {assembly.semester?.includes("spring") ? "spring" : "fall"}.
                </div>
              </div>
              <Link
                to="/dashboard/manifest?tab=cohort"
                data-testid="passive-assembly-open"
                className="shrink-0 inline-flex items-center gap-1.5 text-[12px] font-bold text-teal-700 hover:text-teal-800 px-3 py-1.5 rounded-full border border-teal-500/30 hover:border-teal-500/50 bg-white"
              >
                Open room <ArrowRight size={12} />
              </Link>
            </div>
          )}

          {sameCampus.length > 0 && (
            <PeerGroup
              testid="passive-group-same-campus"
              label="Same campus"
              peers={sameCampus}
              onSayHi={sayHi}
              saying={saying}
            />
          )}
          {nearby.length > 0 && (
            <PeerGroup
              testid="passive-group-nearby"
              label={`Nearby · ${hostCity || nearby[0].host_city} · same term`}
              peers={nearby}
              onSayHi={sayHi}
              saying={saying}
            />
          )}

          {/* Quiet footer link — full Cohort surface is one tap away, but
              underweighted vs the primary "Say hi" actions above. */}
          <div className="border-t border-ink/10 px-4 py-3 md:px-5 flex items-center justify-between">
            <div className="text-[11.5px] text-ink/60 leading-snug">
              Discovery is passive. Replies from peers land in Cohort.
            </div>
            <Link
              to="/dashboard/manifest?tab=matches"
              data-testid="passive-open-manifest"
              className="text-[12px] font-bold text-elec hover:underline shrink-0"
            >
              Open Cohort <ArrowRight size={11} className="inline align-baseline" />
            </Link>
          </div>
        </div>
      )}
    </section>
  );
}

/* ------------------------------------------------------------------------ */

function PeerGroup({ label, peers, onSayHi, saying, testid }) {
  return (
    <div data-testid={testid}>
      <div className="px-4 md:px-5 pt-3 pb-1.5 text-[10px] uppercase tracking-[0.2em] text-ink/50 font-bold">
        {label}
      </div>
      <ul>
        {peers.slice(0, 8).map((p, i) => {
          const state = saying[p.case_id];
          return (
            <li
              key={p.case_id}
              data-testid={`passive-peer-${p.case_id}`}
              className="flex items-center gap-3 px-4 md:px-5 py-2.5 hover:bg-ink/[0.02] transition-colors"
            >
              <Avatar name={p.display_name} i={i} size="lg" />
              <div className="min-w-0 flex-1">
                <div className="text-[14px] font-bold text-ink leading-tight truncate">
                  {p.display_name}
                  {p.is_opted_in && (
                    <span className="ml-2 text-[10px] uppercase tracking-widest font-bold text-teal-600" title="Already active in Cohort">· active</span>
                  )}
                </div>
                <div className="mt-0.5 text-[12px] text-ink/70 leading-snug truncate">
                  {p.home_university || "IIM"} · {p.host_university}
                </div>
              </div>
              <button
                type="button"
                onClick={() => onSayHi(p)}
                data-testid={`passive-say-hi-${p.case_id}`}
                disabled={state === "loading" || state === "sent"}
                className={`shrink-0 inline-flex items-center gap-1.5 text-[12px] font-bold rounded-full px-3.5 py-1.5 transition-all
                  ${state === "sent"
                    ? "bg-teal-500/10 text-teal-700 cursor-default"
                    : state === "loading"
                      ? "bg-elec/10 text-elec cursor-wait"
                      : state === "error"
                        ? "bg-red-50 text-red-600 border border-red-200"
                        : "bg-elec text-white hover:bg-elec/90 active:bg-elec"}`}
              >
                {state === "sent"
                  ? <><Check size={12}/> Said hi</>
                  : state === "loading"
                    ? "Sending…"
                    : state === "error"
                      ? "Try again"
                      : <><MessageCircle size={12}/> Say hi</>}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
